import { useState } from "react";
import { Table } from "antd";
import { Link } from "react-router-dom";
import { env } from "../../../../env/env";
import { MenuSummaryDto } from "../../../../domain/DataKitDto";
import { DataKitMenuView } from "./DataKitMenuView";
import "./DataKitMenusTab.scss";
import { useMenuLocation } from "./menuLocation";

const MENUS_PATH = () => `${env.dashboardPath}/account/data-kits?tab=menus`;

interface Props {
  /** The menus (designs) using this library's items. */
  menus: MenuSummaryDto[];
}

/**
 * A library's Menus tab: the menus that use its items (Oct 12: menus come from designs - each
 * design using library items is a menu, named after it). Read-only: a row opens the menu's view.
 * Final Menus is gone; this list is those designs.
 */
export function DataKitMenusTab({ menus }: Props) {
  const [viewing, setViewing] = useState<string | null>(null);

  const locationOf = useMenuLocation();
  const rows = [...menus].sort(
    (a, b) => (locationOf(a) || "").localeCompare(locationOf(b) || "") || a.name.localeCompare(b.name)
  );

  const columns = [
    {
      title: "Menu",
      key: "menu",
      render: (_: unknown, menu: MenuSummaryDto) => <span className="menus-usage-name">{menu.name}</span>,
      sorter: (a: MenuSummaryDto, b: MenuSummaryDto) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
      showSorterTooltip: false,
    },
    {
      title: "Location",
      key: "location",
      render: (_: unknown, menu: MenuSummaryDto) =>
        locationOf(menu) ? (
          <span className="menus-usage-location">{locationOf(menu)}</span>
        ) : (
          <span className="menus-usage-empty">--</span>
        ),
    },
    {
      title: "Items from this library",
      dataIndex: "itemCount",
      key: "itemCount",
      width: 200,
      align: "right" as const,
    },
  ];

  return (
    <div className="data-kit-menus-tab">
      <p className="data-kit-menus-hint">
        Menus that use items from this library. See every menu in{" "}
        <Link to={MENUS_PATH()}>Item Libraries &rsaquo; Menus</Link>.
      </p>

      <Table
        className="data-kit-menus-table"
        dataSource={rows}
        columns={columns}
        rowKey="designId"
        pagination={false}
        rowClassName="data-kit-menus-row"
        onRow={(menu) => ({ onClick: () => setViewing(menu.designId) })}
        locale={{
          emptyText: (
            <div className="menus-usage-empty-state">
              <p>No menus yet</p>
            </div>
          ),
        }}
      />

      {viewing && <DataKitMenuView designId={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
